/**
 * RegistroAsistencia Component
 * 
 * Componente para registrar asistencias de estudiantes en un grupo.
 * Muestra tabla con selectores de estado y observaciones.
 * Integrado con APIs del backend.
 * 
 * Estados de asistencia:
 * - PRESENTE (verde)
 * - AUSENTE (rojo)
 * - TARDANZA (amarillo)
 * - JUSTIFICADA (azul)
 * 
 * Basado en Image 19 del diseño
 */

import React, { useState, useEffect, useMemo } from "react";
import styles from "./RegistroAsistencias.module.css";
import { getEstudiantesAsistencias, guardarAsistencias } from "../../api/asistencias";

function RegistroAsistencia({ grupo, fecha, onCambioFecha, onRegresar, onAbrirHistorial, onAbrirReporte }) {
  const [estudiantes, setEstudiantes] = useState([]);
  const [asistencias, setAsistencias] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  
  // Estado para ordenamiento - por defecto alfabético por nombre ascendente
  const [ordenamiento, setOrdenamiento] = useState({ campo: 'nombre', direccion: 'asc' });

  // Estudiantes ordenados según el criterio seleccionado
  const estudiantesOrdenados = useMemo(() => {
    if (!estudiantes.length) return [];
    
    return [...estudiantes].sort((a, b) => {
      let valorA, valorB;
      
      if (ordenamiento.campo === 'nombre') {
        valorA = (a.nombre || '').toLowerCase();
        valorB = (b.nombre || '').toLowerCase();
      } else if (ordenamiento.campo === 'matricula') {
        valorA = a.matricula || '';
        valorB = b.matricula || '';
      }
      
      if (ordenamiento.direccion === 'asc') {
        return valorA < valorB ? -1 : valorA > valorB ? 1 : 0;
      } else {
        return valorA > valorB ? -1 : valorA < valorB ? 1 : 0;
      }
    });
  }, [estudiantes, ordenamiento]);

  // Función para cambiar ordenamiento
  const handleOrdenamiento = (campo) => {
    setOrdenamiento(prev => {
      if (prev.campo === campo) {
        // Si es el mismo campo, cambiar dirección
        return { campo, direccion: prev.direccion === 'asc' ? 'desc' : 'asc' };
      } else {
        // Si es diferente campo, empezar con ascendente
        return { campo, direccion: 'asc' };
      }
    });
  };

  // Obtener ícono de ordenamiento
  const getOrdenIcon = (campo) => {
    if (ordenamiento.campo !== campo) return '↕️';
    return ordenamiento.direccion === 'asc' ? '↑' : '↓';
  };

  // Cargar estudiantes desde la API
  useEffect(() => {
    const cargarEstudiantes = async () => {
      if (!grupo?.id_grupo) {
        console.log('[RegistroAsistencia] No hay id_grupo en el objeto grupo:', grupo);
        return;
      }
      
      console.log('[RegistroAsistencia] Cargando estudiantes para grupo_id:', grupo.id_grupo, 'Fecha:', fecha);
      
      try {
        setLoading(true);
        const data = await getEstudiantesAsistencias(grupo.id_grupo, fecha, 1);
        console.log('[RegistroAsistencia] Estudiantes cargados:', data);
        
        setEstudiantes(data.estudiantes || []);
        
        // Inicializar asistencias con datos existentes
        const asistenciasIniciales = {};
        (data.estudiantes || []).forEach(est => {
          asistenciasIniciales[est.id_estudiante_grupo] = {
            estado: est.asistencia?.estado || 'PRESENTE',
            observaciones: est.asistencia?.observaciones || ''
          };
        });
        setAsistencias(asistenciasIniciales);
      } catch (error) {
        console.error('[RegistroAsistencia] Error al cargar estudiantes:', error);
        setMensaje({ tipo: 'error', texto: 'Error al cargar estudiantes del grupo' });
      } finally {
        setLoading(false);
      }
    };
    
    cargarEstudiantes();
  }, [grupo, fecha]);

  const handleCambioEstado = (estudianteGrupoId, nuevoEstado) => {
    setAsistencias(prev => ({
      ...prev,
      [estudianteGrupoId]: {
        ...prev[estudianteGrupoId],
        estado: nuevoEstado
      }
    }));
  };

  const handleCambioObservaciones = (estudianteGrupoId, observaciones) => {
    setAsistencias(prev => ({
      ...prev,
      [estudianteGrupoId]: {
        ...prev[estudianteGrupoId],
        observaciones
      }
    }));
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setMensaje({ tipo: '', texto: '' });
    
    try {
      // Preparar datos para enviar
      const asistenciasArray = estudiantes.map(est => ({
        id_estudiante_grupo: est.id_estudiante_grupo,
        estado: asistencias[est.id_estudiante_grupo]?.estado || 'PRESENTE',
        observaciones: asistencias[est.id_estudiante_grupo]?.observaciones || ''
      }));
      
      const resultado = await guardarAsistencias({
        id_grupo: grupo.id_grupo,
        fecha: fecha,
        parcial: 1,
        asistencias: asistenciasArray
      });
      
      console.log('[RegistroAsistencia] Resultado guardado:', resultado);
      setMensaje({ tipo: 'success', texto: resultado.message || 'Asistencias guardadas exitosamente' });
    } catch (error) {
      console.error('[RegistroAsistencia] Error al guardar:', error);
      setMensaje({ tipo: 'error', texto: error.message || 'Error al guardar asistencias' });
    } finally {
      setGuardando(false);
    }
  };

  const getEstadoClass = (estado) => {
    const clases = {
      'PRESENTE': styles.estadoPresente,
      'AUSENTE': styles.estadoAusente,
      'TARDANZA': styles.estadoTardanza,
      'JUSTIFICADA': styles.estadoJustificada
    };
    return clases[estado] || styles.estadoPresente;
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const date = new Date(fechaStr + 'T00:00:00');
    return date.toLocaleDateString('es-MX', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className={styles.registroContainer}>
      {/* Card de Búsqueda (minimizado) */}
      <div className={styles.busquedaCard}>
        <div className={styles.busquedaHeader}>
          <div className={styles.busquedaIcono}>
            <span>🔍</span>
          </div>
          <h2 className={styles.busquedaTitulo}>Búsqueda de Grupo</h2>
        </div>

        {/* Filtros - Fecha editable */}
        <div className={styles.filtrosContainer}>
          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Campus</label>
            <select className={styles.filtroSelect} disabled>
              <option>{grupo?.campus || 'Sin campus'}</option>
            </select>
          </div>

          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Fecha</label>
            <input
              type="date"
              className={styles.filtroSelect}
              value={fecha}
              onChange={(e) => onCambioFecha && onCambioFecha(e.target.value)}
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className={styles.botonesAccion}>
          <button 
            className={styles.buscarButton}
            onClick={onRegresar}
          >
            BUSCAR
          </button>
          <button 
            className={styles.regresarButton}
            onClick={onRegresar}
          >
            REGRESAR
          </button>
        </div>
      </div>

      {/* Card de Grupo */}
      <div className={styles.grupoCard}>
        <div className={styles.grupoHeader}>
          <div className={styles.grupoIcono}>
            <span>👥</span>
          </div>
          <div className={styles.grupoInfo}>
            <h3 className={styles.grupoTitulo}>
              Grupo: {grupo?.codigo} - {grupo?.nombre}
            </h3>
            <p className={styles.grupoHorario}>
              Horario: {grupo?.horario || 'Sin horario asignado'}
            </p>
            <p className={styles.grupoFecha}>
              Fecha: {formatFecha(fecha)}
            </p>
          </div>
        </div>

        {/* Mensaje de estado */}
        {mensaje.texto && (
          <div className={mensaje.tipo === 'error' ? styles.errorMessage : styles.successMessage}>
            {mensaje.texto}
          </div>
        )}

        {/* Tabla de Asistencias */}
        <div className={styles.tablaAsistenciasContainer}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <p>Cargando estudiantes...</p>
            </div>
          ) : estudiantes.length === 0 ? (
            <div className={styles.emptyContainer}>
              <p>No hay estudiantes inscritos en este grupo.</p>
            </div>
          ) : (
            <table className={styles.tablaAsistencias}>
              <thead>
                <tr className={styles.tablaAsistenciasHeader}>
                  <th 
                    onClick={() => handleOrdenamiento('nombre')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Ordenar por nombre"
                  >
                    Estudiante {getOrdenIcon('nombre')}
                  </th>
                  <th 
                    onClick={() => handleOrdenamiento('matricula')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Ordenar por matrícula"
                  >
                    Matrícula {getOrdenIcon('matricula')}
                  </th>
                  <th>Asistencia</th>
                  <th>Observaciones</th>
                  <th>Historial</th>
                </tr>
              </thead>
              <tbody>
                {estudiantesOrdenados.map((estudiante) => (
                  <tr key={estudiante.id_estudiante_grupo} className={styles.tablaAsistenciasFila}>
                    <td className={styles.estudianteNombre}>
                      {estudiante.nombre}
                    </td>
                    <td>{estudiante.matricula}</td>
                    <td>
                      <select
                        className={`${styles.selectAsistencia} ${getEstadoClass(asistencias[estudiante.id_estudiante_grupo]?.estado)}`}
                        value={asistencias[estudiante.id_estudiante_grupo]?.estado || 'PRESENTE'}
                        onChange={(e) => handleCambioEstado(estudiante.id_estudiante_grupo, e.target.value)}
                      >
                        <option value="PRESENTE">Presente</option>
                        <option value="AUSENTE">Ausente</option>
                        <option value="TARDANZA">Tardanza</option>
                        <option value="JUSTIFICADA">Justificada</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className={styles.inputObservaciones}
                        value={asistencias[estudiante.id_estudiante_grupo]?.observaciones || ''}
                        onChange={(e) => handleCambioObservaciones(estudiante.id_estudiante_grupo, e.target.value)}
                        placeholder=""
                      />
                    </td>
                    <td>
                      <button 
                        className={styles.historialButton}
                        onClick={() => onAbrirHistorial && onAbrirHistorial(estudiante)}
                        title="Ver historial"
                      >
                        📋
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Botones de acción inferior */}
        <div className={styles.botonesInferiores}>
          <button 
            className={styles.guardarButton}
            onClick={handleGuardar}
            disabled={guardando || loading || estudiantes.length === 0}
          >
            {guardando ? 'GUARDANDO...' : 'GUARDAR ASISTENCIAS'}
          </button>
          <button 
            className={styles.reporteButton}
            onClick={onAbrirReporte}
          >
            REPORTE DE ASISTENCIAS
          </button>
        </div>
      </div>
    </div>
  );
}

export default RegistroAsistencia;
